/**
 * useReferenceValues — the single place that fetches admin-configurable
 * reference data (player_position, season, competition_stage, match_type,
 * venue_surface). Every dropdown/consumer goes through this hook (or the
 * shared `ReferenceValueSelect` component, which itself uses this hook) —
 * no component should call `lib/referenceDataApi.ts` directly.
 *
 * Deprecated-value rule: pass `includeInactive: true` on any form that can
 * display/edit an EXISTING record, so a value the record already stores —
 * even if since deactivated, or not present in the API at all (a stray
 * legacy string) — can still be resolved and kept selected. See
 * `withCurrentValuePreserved`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ReferenceCategory,
  ReferenceValue,
  ReferenceValueCreatePayload,
  ReferenceValueUpdatePayload,
  createReferenceValue,
  getReferenceValues,
  reorderReferenceValues,
  updateReferenceValue,
} from "../lib/referenceDataApi";

const QUERY_KEY = "reference-values";

export function useReferenceValues(
  category: ReferenceCategory,
  options?: { includeInactive?: boolean },
) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery({
    queryKey: [QUERY_KEY, category, includeInactive],
    queryFn: () => getReferenceValues(category, includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

function invalidateCategory(
  queryClient: ReturnType<typeof useQueryClient>,
  category: ReferenceCategory,
) {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEY, category] });
}

export function useCreateReferenceValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReferenceValueCreatePayload) =>
      createReferenceValue(payload),
    onSuccess: (created) => invalidateCategory(queryClient, created.category),
  });
}

export function useUpdateReferenceValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ReferenceValueUpdatePayload;
    }) => updateReferenceValue(id, payload),
    onSuccess: (updated) => invalidateCategory(queryClient, updated.category),
  });
}

export function useReorderReferenceValues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      category,
      orderedIds,
    }: {
      category: ReferenceCategory;
      orderedIds: string[];
    }) => reorderReferenceValues(category, orderedIds),
    onSuccess: (_data, variables) =>
      invalidateCategory(queryClient, variables.category),
  });
}

/**
 * Read-only display sites (a stored record rendered as plain text, not a
 * select) — resolves a code to its API label for the current language,
 * falling back to the raw stored string when the code is deactivated or
 * entirely unknown to the reference collection (never drop it, same rule
 * as `withCurrentValuePreserved`, just for text instead of an option list).
 * Fetches with `includeInactive: true` for that reason.
 */
export function useReferenceLabel(category: ReferenceCategory) {
  const { i18n } = useTranslation();
  const { data } = useReferenceValues(category, { includeInactive: true });

  return (code: string | undefined | null): string => {
    if (!code) return "";
    const known = data?.find((v) => v.code === code);
    return known ? resolveReferenceLabel(known, i18n.language) : code;
  };
}

/** Label for the given i18n language ("en"/"es*"), falling back to English. */
export function resolveReferenceLabel(
  value: Pick<ReferenceValue, "labels">,
  language: string | undefined,
): string {
  const lang: "en" | "es" = language?.toLowerCase().startsWith("es")
    ? "es"
    : "en";
  return value.labels[lang] || value.labels.en;
}

export interface ReferenceSelectOption {
  code: string;
  label: string;
  /** True when this option exists only because it's the record's current
   *  value and is either deactivated or entirely unknown to the reference
   *  collection (a legacy stored string) — never offered for a NEW pick. */
  isDeprecated: boolean;
}

/**
 * Builds the option list for a select: every active value, plus the
 * record's current code if it isn't already among the active ones — so a
 * deprecated or unrecognized stored value is always shown/selected, never
 * silently dropped. `all` must come from an `includeInactive: true` fetch
 * for the deprecated case to resolve to a real label instead of the raw code.
 */
export function withCurrentValuePreserved(
  all: ReferenceValue[] | undefined,
  currentCode: string | undefined | null,
  language: string | undefined,
): ReferenceSelectOption[] {
  const list = all ?? [];
  const active = list.filter((v) => v.is_active);
  const options: ReferenceSelectOption[] = active.map((v) => ({
    code: v.code,
    label: resolveReferenceLabel(v, language),
    isDeprecated: false,
  }));

  if (currentCode && !active.some((v) => v.code === currentCode)) {
    const known = list.find((v) => v.code === currentCode);
    options.push({
      code: currentCode,
      label: known ? resolveReferenceLabel(known, language) : currentCode,
      isDeprecated: true,
    });
  }

  return options;
}
